import extend from "lodash/extend";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import { getSnapshot, onAction, onPatch } from "mobx-state-tree";

import { serializeQueryParameters } from "util/string-utils";
import Persistence, { PERMSTORE_ID } from "pages/common/lib/grabyo-persistence";
import Backbone from "pages/common/lib/backbone-helpers";

import { HeapAnalytics } from "page/analytics";

import Constants from "const/constants";
import type { Stream } from "api/Stream.types";
import type { Organization } from "api/organizations.types";
import type { Campaign } from "api/Campaign.types";
import type { LoggedInUser, User } from "api/User.types";
import type { GrabyoApi } from "api/base";
import { initializeIntercom, updateIntercom } from "util/helpdesk-utils";

import ConnectModelToStore from "../lib/migration/connectModelToStore";
import ConnectCollectionToStore from "../lib/migration/connectCollectionToStore";

import type {
    FeaturesConfigInterface,
    StoreInterface,
    StreamInterface
} from "../types/page";
import type { BaseWrappedPageProps, PageProps } from "../../../../pages/_app";

import { OrganizationStore } from "pages/organization/state/store/organization-store";
import { CampaignsStore } from "pages/index/state/store/campaign/campaigns-store";
import { StreamsStore } from "pages/index/state/store/stream/streams-store";

import StreamModel from "./model/stream";
import UserModel from "./model/user";
import OrganizationModel from "./model/organization";
import FeaturesConfigModel from "./model/featuresconfig";

import HomeUIStore from "./store/home/home-ui-store";
import { AppStore } from "./store/app/app-store";
import { UsersStore } from "./store/user/users-store";
import { UserStore } from "./store/user/user-store";

import UsersCollection from "./collection/users";
import StreamCollection from "./collection/streams";
import ContextCollection, {
    contextModelConstructor
} from "./collection/contexts";

export const generateStreamContextParam = function (
    app: AppInterface,
    queryParams: Record<string, string | string[]>,
    pageName = ``
) {
    if (!pageName.startsWith(`producer`)) {
        delete app.Utils.params.streamId;
    }
    delete app.Utils.params.campaignId;
    delete app.Utils.params.galleryId;
    delete app.Utils.params.channelId;
    delete app.Utils.params.brandId;
    delete app.Utils.params.eventId;

    const params: any = {};

    const streamParam =
        app.Store?.streams?.selectedStream?.streamId ??
        app.Models?.selectedStream?.Id;

    if (streamParam) {
        params.streamId = streamParam;
    }

    const contextParam = app.Models.sidebar.get(`context`);
    if (contextParam) {
        params[contextParam.split(`:`)[0]] = contextParam.split(`:`)[1];
    }

    if (!isUndefined(params.galleryId)) {
        params.campaignId = params.galleryId;
        delete params.galleryId;
    }

    app.Models.sidebar.set(`params`, serializeQueryParameters(params));
    app.Utils.params = extend(queryParams, params);
};

enum Migration {
    Streams = `streams`,
    Campaigns = `campaigns`,
    Users = `users`,
    Organization = `organization`,
    UserCredentials = `userCredentials`
}

export const isMigrationEnabled = (
    props: PageProps,
    migration: Migration,
    featuresConfig: FeaturesConfigInterface
) => {
    if (typeof window === `undefined`) return false;

    let enabledPages = [];
    const { pageName } = props;

    switch (migration) {
        case Migration.Streams:
            enabledPages = [`home.jsp`, `trim.jsp`, `organization`];
            break;
        case Migration.Campaigns:
            enabledPages = [`home.jsp`, `studiolibrary.jsp`, `organization`];
            break;
        case Migration.Users:
            enabledPages = [`home.jsp`, `organization`];
            break;
        case Migration.Organization:
            enabledPages = [`organization`];
            break;
        case Migration.UserCredentials:
            if (
                !featuresConfig.studioFeature.unleashSocialCredentialsMigration
            ) {
                return false;
            }
            return true;
    }

    return enabledPages.includes(pageName);
};

export const hydrateBackboneStreams = (
    streams: Stream[],
    props: BaseWrappedPageProps
) => {
    const { featuresConfig, organizationId, pageName, app } = props;
    const { streams: streamsCollection } = app.Collections;
    const store = app.Store as StoreInterface;

    const setSelectedStream = (
        streamData:
            | StreamInterface
            | StreamInterface[]
            | typeof StreamModel
            | (typeof StreamModel)[]
    ) => {
        if (!streamData || streamData.length === 0) {
            return;
        }
        if (pageName === `trim.jsp`) {
            store.streams.setSelectedStreams(streamData);
        } else {
            let selectedStream;
            if (!Array.isArray(streamData)) {
                selectedStream = streamData;
            } else {
                selectedStream = streamData[0];
            }

            const newStreamId =
                selectedStream.id ||
                selectedStream.streamId ||
                selectedStream.get(`streamId`);
            const oldStreamId =
                app.Models.selectedStream?.streamId ??
                app.Models.selectedStream?.id;

            if (oldStreamId !== newStreamId) {
                app.Models.selectedStream =
                    app.Collections.streams.get(newStreamId);

                if (
                    isMigrationEnabled(props, Migration.Streams, featuresConfig)
                ) {
                    store.streams.setSelectedStreams([newStreamId]);
                }
            }
        }
    };

    const onSelectedStreamChange = (
        streamData: StreamInterface | StreamInterface[] | typeof StreamModel
    ) => {
        if (!streamData) {
            return;
        }

        let selectedStreams: StreamInterface[];
        if (!Array.isArray(streamData)) {
            if (streamData instanceof Backbone.Model) {
                const newStreamId =
                    streamData.id ||
                    //@ts-ignore: need better typing of backbone models
                    streamData.streamId ||
                    streamData.get(`streamId`);

                const selectedStream = store.streams.getStreamById(newStreamId);

                selectedStreams = [selectedStream];
            } else {
                selectedStreams = [streamData];
            }
        } else {
            selectedStreams = streamData;
        }

        const serializedStreams = [];

        setSelectedStream(selectedStreams);

        selectedStreams.forEach((stream) => {
            // we extend the stream w/ the current user organizationId
            // To avoid to remember the stream if the user changes account
            stream.setOrganizationId(app.Models.organization.id);
            const serializedStream = getSnapshot(stream);

            serializedStreams.push(serializedStream);
        });

        setPermStore(PERMSTORE_ID.SELECTED_STREAMS, serializedStreams);
    };

    store.streams?.resetStreams(streams);
    streamsCollection.add(streams);

    const streamIds = [];
    let overrideStream = false;
    if (app.Utils.params.streamId) {
        streamIds.push(app.Utils.params.streamId);
        overrideStream = true;
    } else if (Persistence.PermStore.get(PERMSTORE_ID.SELECTED_STREAM)) {
        const stream = JSON.parse(
            Persistence.PermStore.get(PERMSTORE_ID.SELECTED_STREAM)
        );
        if (stream.organizationId === organizationId) {
            streamIds.push(stream.streamId);

            // migrate local storage to new stream data
            setPermStore(PERMSTORE_ID.SELECTED_STREAMS, [stream]);
            clearPermStore(PERMSTORE_ID.SELECTED_STREAM);
        } else {
            // The session has been invalidated
            // and the user has logged in w/ an other account
            clearPermStore(PERMSTORE_ID.SELECTED_STREAM);
        }
    } else if (Persistence.PermStore.get(PERMSTORE_ID.SELECTED_STREAMS)) {
        const storedStreams = JSON.parse(
            Persistence.PermStore.get(PERMSTORE_ID.SELECTED_STREAMS)
        );

        storedStreams.forEach((stream) => {
            if (stream.organizationId === organizationId) {
                streamIds.push(stream.streamId);
            } else {
                // The session has been invalidated
                // and the user has logged in w/ an other account
                clearPermStore(PERMSTORE_ID.SELECTED_STREAMS);
            }
        });
    }

    const foundStreams = [];
    const serialisedStreams = [];

    streamIds.forEach((streamId) => {
        const st = store.streams.getStreamById(streamId);

        st && foundStreams.push(st);
        if (overrideStream && st) {
            st.setOrganizationId(organizationId);
            const extendedStream = getSnapshot(st);

            serialisedStreams.push(extendedStream);
        }
    });

    setSelectedStream(foundStreams);

    if (overrideStream) {
        setPermStore(PERMSTORE_ID.SELECTED_STREAMS, serialisedStreams);
    }

    onAction(store, (action) => {
        if (
            action.name === `setSelectedStreams` &&
            action.path.includes(`streams`)
        ) {
            onSelectedStreamChange(action.args[0]);
        }
    });

    app.Events.on(`change:stream`, onSelectedStreamChange);

    if (!isUndefined(app.Models.selectedStream)) {
        onSelectedStreamChange(app.Models.selectedStream);
    }
};

export const hydrateBackboneCampaigns = (
    campaigns: Campaign[],
    props: BaseWrappedPageProps
) => {
    const { featuresConfig, organizationId, app } = props;
    const { contexts } = app.Collections;
    const store = app.Store as StoreInterface;

    const setSelectedCampaign = (campaign) => {
        if (isUndefined(campaign)) {
            return;
        }
        const newCampaignId =
            campaign?.id || campaign?.campaignId || campaign?.get(`galleryId`);
        const oldCampaignId =
            app.Models.selectedContext?.galleryId ??
            app.Models.selectedContext?.id;

        if (oldCampaignId !== newCampaignId) {
            const selectedCampaign =
                app.Collections.contexts.get(newCampaignId);
            app.Models.selectedContext = selectedCampaign;
        }

        if (
            isMigrationEnabled(props, Migration.Campaigns, featuresConfig) &&
            store.campaigns.selectedCampaignId !== newCampaignId
        ) {
            store.campaigns?.setSelectedCampaignById(newCampaignId);
        }
    };

    const setCampaignFeaturesConfig = (features) => {
        app.Models.featuresConfig.set(`contextFeaturesConfig`, features);
    };

    const setSidebarCampaign = (compoundId?: string): void => {
        if (compoundId) {
            app.Models.sidebar.set(`context`, compoundId);
        } else if (app.Models.selectedContext) {
            app.Models.sidebar.set(
                `context`,
                `${app.Models.selectedContext.idAttribute}:${app.Models.selectedContext.id}`
            );
        }
    };

    const onSelectedCampaignChange = (campaign) => {
        if (isUndefined(campaign)) {
            clearPermStore(PERMSTORE_ID.SELECTED_CAMPAIGN);
            return;
        }
        setSelectedCampaign(campaign);
        setSidebarCampaign();

        let serializedCampaign;

        if (isMigrationEnabled(props, Migration.Campaigns, featuresConfig)) {
            serializedCampaign = getSnapshot(store.campaigns.selectedCampaign);
        } else {
            serializedCampaign = campaign.toJSON();
        }
        setPermStore(PERMSTORE_ID.SELECTED_CAMPAIGN, serializedCampaign);

        const campaignFeatures =
            campaign instanceof Backbone.Model
                ? campaign.get(`featuresConfig`) || {}
                : campaign.featuresConfig || {};

        setCampaignFeaturesConfig(campaignFeatures);
    };

    if (app.Utils.params) {
        Object.keys(app.Utils.params).forEach((param) => {
            const result = param.match(
                /(?:campaign|gallery|channel|brand|event)Id/
            );
            if (!isNull(result)) {
                setSidebarCampaign(
                    `${result[0].replace(`campaign`, `gallery`)}:${
                        app.Utils.params[param]
                    }`
                );
            }
        });
    }

    let campaignId;
    if (isMigrationEnabled(props, Migration.Campaigns, featuresConfig)) {
        const campaigns2 =
            campaigns?.map((c) => {
                const contextId = c.contextId;
                const type = contextId.split(`:`)[0];
                return {
                    ...c[type],
                    contextId
                };
            }) ?? [];
        store.campaigns?.resetCampaigns(campaigns2);
    } else {
        contexts.add(
            ContextCollection.prototype.parse.call(contexts, campaigns)
        );
    }
    const params = app.Utils.params;
    const paramContextId = params.galleryId || params.campaignId;

    let overrideCampaign = false;
    if (!isUndefined(paramContextId)) {
        campaignId = paramContextId;
        overrideCampaign = true;
    } else if (
        !isUndefined(Persistence.PermStore.get(PERMSTORE_ID.SELECTED_CAMPAIGN))
    ) {
        const campaign = JSON.parse(
            Persistence.PermStore.get(PERMSTORE_ID.SELECTED_CAMPAIGN)
        );

        if (campaign.organizationId === organizationId) {
            campaignId = campaign.galleryId;
        } else {
            // The session has been invalidated
            // and the user has logged in w/ an other account
            clearPermStore(PERMSTORE_ID.SELECTED_CAMPAIGN);
        }
    }
    const ctx = contexts.get(campaignId);
    ctx && setSelectedCampaign(ctx);
    if (overrideCampaign && ctx) {
        const extendedCampaign = extend(ctx.toJSON(), {
            organizationId
        });
        setPermStore(PERMSTORE_ID.SELECTED_CAMPAIGN, extendedCampaign);
        Persistence.PermStore.set(
            PERMSTORE_ID.SELECTED_CAMPAIGN,
            JSON.stringify(extendedCampaign)
        );
    }

    const campaignFeatures =
        app.Models.selectedContext?.get(`featuresConfig`) || {};

    if (app.Models.selectedContext) {
        setCampaignFeaturesConfig(campaignFeatures);
    }

    if (app.Models.selectedContext) {
        setSidebarCampaign();
        setCampaignFeaturesConfig(campaignFeatures);
    }

    onPatch(store.campaigns, (patch) => {
        if (patch.path.includes(`selectedCampaign`)) {
            const selectedCampaignId = patch.value;
            const campaign = store.campaigns.getById(selectedCampaignId);
            onSelectedCampaignChange(campaign);
        }
    });

    app.Events.on(
        `change:campaign change:selectedContext`,
        onSelectedCampaignChange
    );

    app.HomeUIStore.setSelectedContextId(campaignId);

    app.Events.trigger(`campaigns:loaded`, campaigns);
};

export const hydrateBackboneUsers = (
    users: User[],
    props: BaseWrappedPageProps
) => {
    const { featuresConfig, app } = props;
    const store = app.Store as StoreInterface;
    const usersStore = UsersStore.create();
    if (isMigrationEnabled(props, Migration.Users, featuresConfig)) {
        const LinkedUsersCollection = ConnectCollectionToStore({
            collection: UsersCollection,
            store: usersStore,
            _model: UserModel,
            polymorphicModel: null
        });
        app.Collections.users = new LinkedUsersCollection(users);
    } else {
        app.Collections.users = new UsersCollection(users);
    }
    store.setUsers(usersStore);
};

export const initStores = (
    organization: Organization,
    loggedInUser: LoggedInUser,
    props: PageProps,
    app: AppInterface
) => {
    const { featuresConfig } = props;
    const store = app.Store as StoreInterface;

    const userStore = UserStore.create({ ...loggedInUser });

    const LinkedUserModel = ConnectModelToStore(UserModel, userStore);
    const user = new LinkedUserModel({ ...loggedInUser });

    app.Models.user = user;

    //TODO: this is not used and doesn't work anyway...
    //userStore.setSocialCredentials(app.Models.user.socialCredentials);
    store.setUser(userStore);

    const usersStore = UsersStore.create();
    if (isMigrationEnabled(props, Migration.Users, featuresConfig)) {
        const LinkedUsersCollection = ConnectCollectionToStore({
            collection: UsersCollection,
            store: usersStore,
            _model: UserModel,
            polymorphicModel: null
        });
        app.Collections.users = new LinkedUsersCollection([]);
    } else {
        app.Collections.users = new UsersCollection([]);
    }
    store.setUsers(usersStore);

    const streamsStore = StreamsStore.create();
    if (isMigrationEnabled(props, Migration.Streams, featuresConfig)) {
        const LinkedStreamCollection = ConnectCollectionToStore({
            collection: StreamCollection,
            store: streamsStore,
            _model: StreamModel,
            polymorphicModel: null
        });
        app.Collections.streams = new LinkedStreamCollection([]);
    } else {
        app.Collections.streams = new StreamCollection([]);
    }
    store.setStreams(streamsStore);

    const campaignsStore = CampaignsStore.create();
    if (isMigrationEnabled(props, Migration.Campaigns, featuresConfig)) {
        const LinkedContextCollection = ConnectCollectionToStore({
            collection: ContextCollection,
            store: campaignsStore,
            _model: null,
            polymorphicModel: (attrs) => contextModelConstructor(attrs)
        });
        app.Collections.contexts = new LinkedContextCollection([]);
    } else {
        app.Collections.contexts = new ContextCollection([]);
    }
    store.setCampaigns(campaignsStore);

    let organizationStore;
    if (organization) {
        if (isMigrationEnabled(props, Migration.Organization, featuresConfig)) {
            organizationStore = OrganizationStore.create({
                ...organization
            });
            const LinkedOrganizationModel = ConnectModelToStore(
                OrganizationModel,
                organizationStore
            );
            app.Models.organization = new LinkedOrganizationModel(organization);
        } else {
            app.Models.organization = new OrganizationModel(organization);
        }
    } else {
        app.Models.organization = new OrganizationModel();
    }
    store.setOrganization(organizationStore);
};

export const createAppShell = (props: PageProps): AppInterface => {
    const { featuresConfig, queryParameters, organization, pageName } = props;

    const app: AppInterface = {
        Store: AppStore.create(),
        Collections: {
            contexts: {},
            streams: {},
            users: {},
            grabs: {}
        },
        Constants,
        Events: extend({}, Backbone.Events),
        Models: {
            user: {},
            featuresConfig: new FeaturesConfigModel(featuresConfig),
            organization: {},
            sidebar: new Backbone.Model({
                stream: ``,
                context: ``,
                params: ``
            })
        },
        Routers: {},
        Utils: {
            isMigrationEnabled,
            updatePageState: function () {
                if (typeof window === `undefined`) return;
                /**
                 * Feature only available for IE9+
                 * We perform a simple check to not prevent user on IE9 to use Studio
                 */
                if (history.pushState) {
                    const url = new URL(
                        window.location.origin + window.location.pathname
                    );

                    Object.entries(app.Utils.params).forEach(([key, value]) => {
                        if (Array.isArray(value)) {
                            value.forEach((value) =>
                                url.searchParams.append(key, value.toString())
                            );
                        } else {
                            url.searchParams.append(key, value.toString());
                        }
                    });

                    const hash = window.location.hash;

                    history.pushState({}, `Trim page`, url);

                    window.location.hash = hash;
                }
            },
            params: JSON.parse(JSON.stringify(queryParameters))
        },
        Views: {},
        HomeUIStore: HomeUIStore.create()
    };

    if (organization?.featuresConfig) {
        app.Models.featuresConfig.set(
            `organizationFeaturesConfig`,
            organization.featuresConfig
        );
    }

    if (
        !app.Models.featuresConfig.get("featuresConfig").studioFeature
            ?.maxConcurrentBroadcastDestinationsPerStream
    ) {
        if (props.maxConcurrentBroadcastDestinationsPerStream) {
            app.Models.featuresConfig.get(
                "featuresConfig"
            ).studioFeature.maxConcurrentBroadcastDestinationsPerStream =
                parseInt(props.maxConcurrentBroadcastDestinationsPerStream, 10);
        }
    }

    if (queryParameters) {
        generateStreamContextParam(app, queryParameters, pageName);
        app.Models.sidebar.on(`change:stream change:context`, () =>
            generateStreamContextParam(app, queryParameters, pageName)
        );
    }

    return app;
};

export const initCredentialRefreshTimer = (
    loggedInUser: LoggedInUser,
    tokenAgeSeconds: number,
    api: GrabyoApi
) => {
    if (loggedInUser && tokenAgeSeconds) {
        Persistence.PermStore.set(
            `tokenExpiryDate`,
            Date.now() + tokenAgeSeconds * 1000
        );
        setInterval(
            () => api.postJSON(`v2/refresh-token`),
            tokenAgeSeconds * 1000 * 0.8
        ); // Refresh at 80% of TTL to be on the safe side
    }
};

const setPermStore = (permStoreId: string, data: unknown) => {
    Persistence.PermStore.set(permStoreId, JSON.stringify(data));
};

const clearPermStore = (permStoreId: string) => {
    Persistence.PermStore.clear(permStoreId);
};

export const initIntercom = (props: PageProps) => {
    if (!props.enableIntercom) {
        return;
    }
    if (typeof window !== `undefined`) {
        initializeIntercom(window);
        if (window.Intercom) {
            const { loggedInUser, intercomHash, organization } = props;
            if (loggedInUser) {
                updateIntercom(
                    window,
                    loggedInUser,
                    organization,
                    intercomHash
                );
            }
        }
    }
};

export const initAnalytics = (props: PageProps) => {
    if (typeof window !== `undefined`) {
        window.heap.onReady(() => {
            if (HeapAnalytics.isDefined()) {
                const { loggedInUser: user, organization } = props;
                if (!user) {
                    HeapAnalytics.resetIdentity();
                }
                // eg. if logging in as a different user on the same machine
                if (user && user.userId !== HeapAnalytics.getUserId()) {
                    HeapAnalytics.resetIdentity();
                    HeapAnalytics.identify(organization, user);
                }
            }
        });
    }
};

export const getLoginFrontendOrigin = (isLocal: boolean, hostname: string) => {
    if (isLocal) {
        return `http://localhost:3031`;
    }

    const [, grabyoDomain, topLevelDomain] = hostname.split(`.`);
    return `https://login.${grabyoDomain}.${topLevelDomain}`;
};

export const buildLoginUrl = (hostname: string, redirectPath?: string) => {
    const isLocal = hostname.includes("localhost");
    const loginFrontendOrigin = getLoginFrontendOrigin(isLocal, hostname);
    const deploymentId = hostname.split(`.`).shift();
    const params = new URLSearchParams({ discovery: "" });

    // Login frontend comes back to studio by default so no need to provide
    // query param
    if (!isLocal && deploymentId !== "studio") {
        params.append("deploymentId", deploymentId);
    }

    if (redirectPath) {
        params.append("redirectPath", `/${redirectPath}`);
    }

    return `${loginFrontendOrigin}?${params.toString()}`;
};

export const clientRedirectToLogin = (isHttpError: boolean) => {
    const redirectPath = isHttpError
        ? window.location.pathname.split(`/`).pop()
        : undefined;
    const location = buildLoginUrl(window.location.hostname, redirectPath);
    (window as Window).location = location;
};
